/**
 * DatePickerField — a single-date form field with the `"YYYY-MM-DD"` value
 * contract the app's forms are wired to.
 *
 * As of the Signal-DS adoption (PR — see CONVERSION_NOTES) this is a **thin
 * adapter** over the DS `DatePicker` atom (`@scripsteam/scrips-signal-ds`),
 * which owns the styled trigger + portaled (base-ui) popover + month/year
 * dropdown navigation. The adapter exists only to:
 *   • keep the `"YYYY-MM-DD"` string value contract (the DS atom speaks `Date`),
 *     so the 21 existing call sites need zero changes;
 *   • render the app's field label (sentence case, medium weight) (the DS label style differs);
 *   • map the boolean `invalid` border to the DS trigger (the DS models errors
 *     as a message string, not a border-only flag — see the `invalid` note).
 *
 * Bounds are opt-in: pass `max`/`min` to constrain (e.g. `max={today}` for
 * past/today-only fields like DOB). The DS year dropdown spans 1900–2100 unless
 * bounded.
 *
 * For clinical chart dates that accept free-text / durations (DD/MM/YYYY,
 * MM/YYYY, 1Y…), use `DynamicDateInput` instead — this is the plain-date
 * sibling.
 */

import { DatePicker } from '@scripsteam/scrips-signal-ds';
import { X } from 'lucide-react';

export interface DatePickerFieldProps {
  /** Selected date as `"YYYY-MM-DD"`, or `""` when empty. */
  value: string;
  /** Emits `"YYYY-MM-DD"`, or `""` when the selection is cleared. */
  onChange: (yyyyMmDd: string) => void;
  /** Latest selectable date; later dates are disabled. Omit for no upper
   *  bound (the year dropdown still tops out at 2100). Pass `new Date()` for
   *  past/today-only fields (DOB, onset, payment dates). */
  max?: Date;
  /** Earliest selectable date. Omit for no lower bound (back to 1900). */
  min?: Date;
  placeholder?: string;
  disabled?: boolean;
  /** Red border when the field is in an error state (border-only; the message,
   *  if any, is rendered by the caller's field-error element). */
  invalid?: boolean;
  /** Optional field label rendered above the trigger. When set, `className`
   *  applies to the wrapper (not the trigger), so the label + field read as
   *  one block — use for callers that don't already supply their own label.
   *  Omit it when the caller renders its own `<FieldLabel>` / `<Field>`. */
  label?: string;
  id?: string;
  className?: string;
  /** Show an inline clear ("×") affordance when a date is set, so the user can
   *  empty the field at any time (the DS `DatePicker` atom has no clear control
   *  of its own). Defaults to `true` — dates are clearable everywhere; pass
   *  `false` only for a field that must never be emptied once set. Hidden when
   *  `disabled` or when there's no value. */
  clearable?: boolean;
}

/** Parse `"YYYY-MM-DD"` into a local-midnight Date (undefined if malformed). */
function parseYmd(v: string): Date | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return undefined;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? undefined : d;
}

/** Serialize a Date to `"YYYY-MM-DD"` (local calendar day, no TZ shift). */
function toYmd(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function DatePickerField({
  value,
  onChange,
  max,
  min,
  placeholder = 'Select a date',
  disabled = false,
  invalid = false,
  label,
  id,
  className,
  clearable = true,
}: DatePickerFieldProps) {
  // The .NET backend serialises an unset DateTime as its min value
  // (`0001-01-01T00:00:00`). Treat that sentinel as empty so the picker shows
  // the placeholder — never a bogus "01 Jan 0001" — and offers no clear "×".
  const effectiveValue = value && !/^0001-01-01/.test(value) ? value : '';
  const showClear = clearable && !disabled && effectiveValue !== '';

  // The clear "×" is a sibling overlaid on top of the DS trigger (z-10) and
  // inset from the right so it sits to the LEFT of the trigger's calendar
  // glyph. Clicking it empties the field without opening the calendar (the
  // click lands on this button, not the trigger underneath).
  const picker = (
    <div className="relative">
      <DatePicker
        // The DS DatePicker keeps internal selected-date state and does NOT
        // re-render when `value` goes back to `undefined`, so a programmatic
        // clear (the "×") left the old date on the trigger. Keying on the
        // value forces a fresh mount whenever it changes externally — clearing
        // (→ 'empty') and any external set both reflect immediately. Selecting a
        // day in the calendar also remounts, which just closes the popover
        // (the normal pick-and-close behaviour).
        key={effectiveValue || 'empty'}
        id={id}
        value={parseYmd(effectiveValue)}
        onChange={(d) => onChange(d ? toYmd(d) : '')}
        minDate={min}
        maxDate={max}
        placeholder={placeholder}
        disabled={disabled}
        // Border-only error state — the caller's field-error renders the message
        // (DS `invalid` = red trigger border, no helper row; `error` would add a
        // message). First-class prop since scrips-signal-ds@1.27.0 (#169),
        // replacing the earlier scoped `[&_button]` border override.
        invalid={invalid}
        // Match the app's existing display exactly — zero-padded day ("01 Sep
        // 2026", "12 Mar 1990") — rather than the DS default ("Mar 12, 1990").
        // `dd` preserves the old `padStart(2,'0')` so sweep tests asserting the
        // trigger text still pass.
        dateFormat="dd MMM yyyy"
      />
      {showClear ? (
        <button
          type="button"
          aria-label="Clear date"
          // pointerdown stop: base-ui opens the popover on pointerdown, so
          // guard against the trigger opening if the event ever reaches it.
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onChange('');
          }}
          className="absolute right-3 top-1/2 z-10 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded bg-surface-card text-text-muted hover:bg-grey-100 hover:text-text-primary"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      ) : null}
    </div>
  );

  if (!label) return <div className={className}>{picker}</div>;
  return (
    <div className={className}>
      <label
        htmlFor={id}
        className="mb-1 block text-xs font-medium text-text-muted"
      >
        {label}
      </label>
      {picker}
    </div>
  );
}
